import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  FileText,
  Plus,
  Search,
  Download,
  TrendingUp,
  Loader2,
  AlertCircle,
  Eye,
  Edit,
  Trash2,
  Share2,
  Filter,
} from 'lucide-react';
import { useState } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useInvoices, useDeleteInvoice, useInvoice } from '@/hooks/queries/useInvoices';
import { toast } from '@/components/ui/toast/useToast';
import { generateInvoicePDF } from '@/lib/pdfGenerator';
import { InvoiceForm } from '@/components/invoices/InvoiceForm';
import { StockAlertsWidget } from '@/components/dashboard/StockAlertsWidget';

export const Route = createFileRoute('/apps/sales/')({
  beforeLoad: ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/login' });
    }
  },
  component: SalesPage,
});

function SalesPage() {
  const { company } = useAuthStore();
  const navigate = useNavigate();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [editingInvoiceId, setEditingInvoiceId] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);

  const { data: invoices = [], isLoading, error, refetch } = useInvoices({
    status: statusFilter === 'all' ? undefined : statusFilter,
  });
  
  const { data: editingInvoice } = useInvoice(editingInvoiceId || '');
  const deleteInvoice = useDeleteInvoice();

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ar-DZ', {
      style: 'currency',
      currency: 'DZD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const filteredInvoices = invoices.filter((invoice: any) => {
    const searchLower = searchQuery.toLowerCase();
    return (
      invoice.invoice_number?.toLowerCase().includes(searchLower) ||
      invoice.clients?.name?.toLowerCase().includes(searchLower)
    );
  });

  const stats = {
    total: invoices.length,
    totalAmount: invoices.reduce((sum: number, inv: any) => sum + (inv.total || 0), 0),
    draft: invoices.filter((inv: any) => inv.status === 'draft').length,
    paid: invoices.filter((inv: any) => inv.status === 'paid').length,
  };

  const handleView = (invoiceId: string) => {
    navigate({ to: `/invoices/${invoiceId}` });
  };

  const handleEdit = (invoiceId: string) => {
    setEditingInvoiceId(invoiceId);
    setFormOpen(true);
  };

  const handleDownloadPDF = async (invoice: any) => {
    try {
      await generateInvoicePDF(invoice);
      toast.success('تم التنزيل', 'PDF téléchargé');
    } catch (error) {
      console.error('PDF error:', error);
      toast.error('خطأ', 'Erreur PDF');
    }
  };

  const handleDelete = async (id: string) => {
    if (deleteConfirmId === id) {
      try {
        await deleteInvoice.mutateAsync(id);
        toast.success('تم الحذف', 'Facture supprimée');
        setDeleteConfirmId(null);
      } catch (error) {
        toast.error('خطأ', 'Erreur');
      }
    } else {
      setDeleteConfirmId(id);
      setTimeout(() => setDeleteConfirmId(null), 3000);
    }
  };

  const statusColors = {
    draft: 'bg-orange-100 text-orange-800',
    partial: 'bg-yellow-100 text-yellow-800',
    paid: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800',
  };

  const statusLabels = {
    draft: 'Brouillon',
    partial: 'Partiel',
    paid: 'Payée',
    cancelled: 'Annulée',
  };

// const statusColors = {
//   draft: 'bg-orange-100 text-orange-800',
//   sent: 'bg-blue-100 text-blue-800',
//   partial: 'bg-yellow-100 text-yellow-800',  // ADD THIS
//   paid: 'bg-green-100 text-green-800',
//   overdue: 'bg-red-100 text-red-800',
//   cancelled: 'bg-red-100 text-red-800',
// };

// const statusLabels = {
//   draft: 'مسودة / Brouillon',
//   sent: 'مرسلة / Envoyée',
//   partial: 'دفع جزئي / Partiel',  // ADD THIS
//   paid: 'مدفوعة / Payée',
//   overdue: 'متأخرة / En retard',
//   cancelled: 'ملغاة / Annulée',
// };

  return (
    <>
      <DashboardLayout>
        <div className="p-6 space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold">المبيعات / Ventes</h1>
              <p className="text-gray-500 mt-1">Gérez vos factures et ventes</p>
            </div>
            <Button onClick={() => navigate({ to: '/draft-invoice' })}>
              <Plus className="w-5 h-5 mr-2" />
              Nouvelle facture
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold">{stats.total}</div>
                <p className="text-sm text-gray-500">Total Factures</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold text-emerald-600">{formatCurrency(stats.totalAmount)}</div>
                <p className="text-sm text-gray-500">Montant Total</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold text-orange-600">{stats.draft}</div>
                <p className="text-sm text-gray-500">Brouillons</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold text-green-600">{stats.paid}</div>
                <p className="text-sm text-gray-500">Payées</p>
              </CardContent>
            </Card>
          </div>

          {/* Stock Alerts Widget */}
          <StockAlertsWidget />

          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Rechercher..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="draft">Brouillon</SelectItem>
                <SelectItem value="paid">Payée</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Liste des factures ({filteredInvoices.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin" />
                </div>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3">N°</th>
                      <th className="text-left p-3">Client</th>
                      <th className="text-left p-3">Date</th>
                      <th className="text-left p-3">Montant</th>
                      <th className="text-left p-3">Statut</th>
                      <th className="text-left p-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInvoices.map((invoice: any) => (
                      <tr key={invoice.id} className="border-b hover:bg-gray-50">
                        <td className="p-3">{invoice.invoice_number}</td>
                        <td className="p-3">{invoice.clients?.name}</td>
                        <td className="p-3">{formatDate(invoice.created_at)}</td>
                        <td className="p-3">{formatCurrency(invoice.total)}</td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded text-xs ${statusColors[invoice.status as keyof typeof statusColors]}`}>
                            {statusLabels[invoice.status as keyof typeof statusLabels]}
                          </span>
                        </td>
                        <td className="p-3">
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => handleView(invoice.id)}>
                              <Eye className="w-4 h-4" />
                            </Button>
                            {invoice.status === 'draft' && (
                              <Button variant="ghost" size="sm" onClick={() => handleEdit(invoice.id)}>
                                <Edit className="w-4 h-4" />
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => handleDownloadPDF(invoice)}>
                              <FileText className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(invoice.id)}>
                              {deleteConfirmId === invoice.id ? '✓' : <Trash2 className="w-4 h-4" />}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>

      <InvoiceForm
        open={formOpen}
        onOpenChange={(open) => {
          setFormOpen(open);
          if (!open) setEditingInvoiceId(null);
        }}
        invoice={editingInvoice || null}
        onSuccess={() => {
          setFormOpen(false);
          setEditingInvoiceId(null);
          refetch();
        }}
      />
    </>
  );
}
